/**
 * @fileoverview Match3 Sound Effects Hook
 * @description Manages sound effects for the Match3 game with user preference support
 */

import { useState, useEffect, useCallback, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

/** Storage key for sound settings */
const MATCH3_SOUND_KEY = "@staatsen_match3_sounds_enabled";

/** 
 * Check if expo-av is available (native module loaded)
 * This will be false until the dev client is rebuilt
 */
let Audio: typeof import("expo-av").Audio | null = null;
let isAudioAvailable = false;

try {
  // Dynamic import to prevent crash when native module isn't available
  const expoAv = require("expo-av");
  Audio = expoAv.Audio;
  isAudioAvailable = true;
} catch (error) {
  console.warn("[Match3Sounds] expo-av not available - sounds disabled. Rebuild your dev client to enable sounds.");
  isAudioAvailable = false;
}

/** Sound file paths */
const SOUND_FILES = {
  match: require("@/assets/game_sounds/match3_matching_sound.mp3"),
  bonus: require("@/assets/game_sounds/bonus_notification_sound.mp3"),
} as const;

interface UseMatch3SoundsReturn {
  /** Whether sounds are enabled */
  soundsEnabled: boolean;
  /** Toggle sounds on/off */
  toggleSounds: () => Promise<void>;
  /** Set sounds enabled/disabled */
  setSoundsEnabled: (enabled: boolean) => Promise<void>;
  /** Play the match sound effect */
  playMatchSound: () => Promise<void>;
  /** Play the bonus/special activation sound effect */
  playBonusSound: () => Promise<void>;
  /** Whether sounds are loaded and ready */
  isReady: boolean;
  /** Whether the native audio module is available */
  isAudioAvailable: boolean;
}

/**
 * Hook for managing Match3 game sound effects
 * 
 * Features:
 * - Per-game sound preference (stored in AsyncStorage)
 * - Preloaded sounds for low latency
 * - Separate match and bonus sounds
 * - Graceful fallback when expo-av native module isn't available
 * 
 * @example
 * const { soundsEnabled, toggleSounds, playMatchSound, playBonusSound } = useMatch3Sounds();
 * 
 * // When user makes a match
 * playMatchSound();
 * 
 * // When bomb/lightning/rainbow activates
 * playBonusSound();
 */
export const useMatch3Sounds = (): UseMatch3SoundsReturn => {
  const [soundsEnabled, setSoundsEnabledState] = useState(true);
  const [isReady, setIsReady] = useState(false);

  // Sound objects - using any type since Audio.Sound may not be available
  const matchSoundRef = useRef<any>(null);
  const bonusSoundRef = useRef<any>(null);

  /**
   * Load sound preference from storage
   */
  const loadPreference = useCallback(async () => {
    try {
      const stored = await AsyncStorage.getItem(MATCH3_SOUND_KEY);
      if (stored !== null) {
        setSoundsEnabledState(stored === "true");
      }
    } catch (error) {
      console.error("[Match3Sounds] Error loading preference:", error);
    }
  }, []);

  /**
   * Preload all sound files
   */
  const preloadSounds = useCallback(async () => {
    if (!isAudioAvailable || !Audio) {
      // Skip preloading if audio isn't available
      return;
    }

    try {
      // Configure audio mode for game sounds
      await Audio.setAudioModeAsync({
        playsInSilentModeIOS: false,
        staysActiveInBackground: false,
        shouldDuckAndroid: true,
      });

      // Load match sound
      const { sound: matchSound } = await Audio.Sound.createAsync(
        SOUND_FILES.match,
        { shouldPlay: false, volume: 0.7 }
      );
      matchSoundRef.current = matchSound;

      // Load bonus sound
      const { sound: bonusSound } = await Audio.Sound.createAsync(
        SOUND_FILES.bonus,
        { shouldPlay: false, volume: 0.8 }
      );
      bonusSoundRef.current = bonusSound;

      setIsReady(true);
    } catch (error) {
      console.error("[Match3Sounds] Error preloading sounds:", error);
    }
  }, []);

  /**
   * Unload sounds on cleanup
   */
  const unloadSounds = useCallback(async () => {
    try {
      if (matchSoundRef.current) {
        await matchSoundRef.current.unloadAsync();
        matchSoundRef.current = null;
      }
      if (bonusSoundRef.current) {
        await bonusSoundRef.current.unloadAsync();
        bonusSoundRef.current = null;
      }
    } catch (error) {
      console.error("[Match3Sounds] Error unloading sounds:", error);
    }
  }, []);

  /**
   * Initialize sounds on mount
   */
  useEffect(() => {
    loadPreference();
    
    if (isAudioAvailable) {
      preloadSounds();
    }

    return () => {
      if (isAudioAvailable) {
        unloadSounds();
      }
    };
  }, [loadPreference, preloadSounds, unloadSounds]);

  /**
   * Toggle sounds on/off
   */
  const toggleSounds = useCallback(async () => {
    const newValue = !soundsEnabled;
    setSoundsEnabledState(newValue);
    try {
      await AsyncStorage.setItem(MATCH3_SOUND_KEY, String(newValue));
    } catch (error) {
      console.error("[Match3Sounds] Error saving preference:", error);
    }
  }, [soundsEnabled]);

  /**
   * Set sounds enabled/disabled
   */
  const setSoundsEnabled = useCallback(async (enabled: boolean) => {
    setSoundsEnabledState(enabled);
    try {
      await AsyncStorage.setItem(MATCH3_SOUND_KEY, String(enabled));
    } catch (error) {
      console.error("[Match3Sounds] Error saving preference:", error);
    }
  }, []);

  /**
   * Play the match sound effect
   */
  const playMatchSound = useCallback(async () => {
    if (!soundsEnabled || !isReady || !matchSoundRef.current || !isAudioAvailable) return;

    try {
      // Rewind to start and play
      await matchSoundRef.current.setPositionAsync(0);
      await matchSoundRef.current.playAsync();
    } catch (error) {
      console.error("[Match3Sounds] Error playing match sound:", error);
    }
  }, [soundsEnabled, isReady]);

  /**
   * Play the bonus/special activation sound effect
   */
  const playBonusSound = useCallback(async () => {
    if (!soundsEnabled || !isReady || !bonusSoundRef.current || !isAudioAvailable) return;

    try {
      // Rewind to start and play
      await bonusSoundRef.current.setPositionAsync(0);
      await bonusSoundRef.current.playAsync();
    } catch (error) {
      console.error("[Match3Sounds] Error playing bonus sound:", error);
    }
  }, [soundsEnabled, isReady]);

  return {
    soundsEnabled,
    toggleSounds,
    setSoundsEnabled,
    playMatchSound,
    playBonusSound,
    isReady,
    isAudioAvailable,
  };
};

export default useMatch3Sounds;
